import styled from '@emotion/styled';
import { Variables } from '@enter/core';

export const TitleRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
`;

export const Subtitle = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 13px;
    margin-top: 4px;
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

/**
 * What a tab shows when it cannot show a table yet.
 *
 * It states what is missing and why rather than rendering an empty table. An empty table asserts
 * "no rejections"; this asserts "not groupable yet", which is the true statement while FE-73 · B1
 * is open.
 */
export const PendingPanel = styled.div`
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.nx8};
    border-radius: ${Variables.Rounded.medium};
    padding: 32px;
    width: 100%;
`;

export const PendingTitle = styled.div`
    color: ${Variables.Swatches.Gray11};
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 8px;
`;

export const PendingBody = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 13px;
    line-height: 1.6;
    max-width: 720px;
`;

/** Wrapper that carries the "why is this disabled" title a disabled button cannot show itself. */
export const ActionSlot = styled.span`
    display: inline-flex;
`;

/** The controls on the tab row, as the design sets them: search, the date range, Filters, Sort. */
export const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

/**
 * The panel the tabs control. The page row cannot carry the tabpanel role itself (it passes on only a class
 * name), so this wraps its content and lays it out the way the row does: nothing on screen moves.
 *
 * `width: 100%` holds it to the row. The row is a WRAPPING column flex container, and in a wrapping
 * container a stretched item takes the width of its line — which is the widest item on it, not the
 * row. So the Rejections table, with its disabled per-claim columns, made the panel 1310px in a 1096px
 * row, and the page clipped the overflow: the table's right edge and the notice's text cut off
 * mid-word (measured 2026-10-01; `min-width: 0` alone changed nothing). Held to the row, the card's own
 * `overflow: auto` scrolls the table instead.
 */
export const TabPanel = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    min-width: 0;
`;

/** Where the search field sits in the toolbar. The field itself is the core `BaseInput`, compact, with its icon. */
export const SearchSlot = styled.div`
    flex: none;
    width: 234px;
`;

/**
 * The table's own header, as the design measures it: 12px by 16px inside the card, a rule underneath. The card has no
 * padding of its own (the table runs edge to edge), so everything else inside it has to bring its inset.
 */
export const SectionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 16px;
    border-bottom: 1px solid ${Variables.Swatches.Gray3};
`;

export const SectionTitle = styled.div`
    color: ${Variables.Swatches.Gray11};
    font-size: 14px;
    font-weight: 600;
`;

export const SectionSub = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 12px;
    margin-top: 2px;
`;

/** The table's controls, right of its title: the routine-adjustment filter, then Group by. */
export const SectionControls = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
`;

/** The design's table type size, set once, and its header row on the lightest grey. */
/**
 * `stale`: the rows on screen belong to the week being replaced. Dimmed, and not clickable — a click
 * would open the drill-down of an OLD row against the NEW week's dates.
 */
export const TableFrame = styled.div<{ stale?: boolean }>`
    font-size: 13px;
    opacity: ${({ stale }) => (stale ? 0.4 : 1)};
    pointer-events: ${({ stale }) => (stale ? 'none' : 'auto')};
    transition: opacity 0.2s ease;

    .table-header-with-filter {
        background-color: ${Variables.Swatches.Gray1};
    }
`;

export const TableHeader = styled.div`
    color: ${Variables.Swatches.Gray6};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

export const TableHeaderRight = styled(TableHeader)`
    width: 100%;
    text-align: right;
`;

export const TableCell = styled.div`
    color: ${Variables.Swatches.Gray9};
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/**
 * Reason text, a step lighter than the code beside it, as the design sets it — and wrapped, as the design has it:
 * a CARC sentence runs to a dozen words, and the row is worth the height if the whole sentence can be read.
 */
export const ReasonCell = styled(TableCell)`
    color: ${Variables.Swatches.Gray8};
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    line-height: 1.45;
    padding: 4px 0;
`;

/**
 * The code, as the design's grey chip. Monospaced, because a code is read character by character and
 * `CO:16` must not look like `CO:161`. `Variables.Fonts` has only `calibre`, so the stack is spelled
 * out here — a font literal, not a colour one (NS-2 governs colour). Worth a `mono` token in the
 * design system if more code-bearing surfaces appear.
 */
export const CodeChip = styled.span`
    display: inline-block;
    padding: 1px 5px;
    border-radius: 4px;
    background-color: ${Variables.Swatches.Gray2};
    color: ${Variables.Swatches.Gray11};
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
`;

export const NumberCell = styled.div`
    color: ${Variables.Swatches.Gray9};
    font-size: 13px;
    width: 100%;
    text-align: right;
    font-variant-numeric: tabular-nums;
`;

/** The per-row worker action, right-aligned like the design's WORKER column. */
export const WorkerCell = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;

    button {
        white-space: nowrap;
    }
`;

/** A money cell the report cannot fill yet: a muted dash, with the reason on hover — never a zero. */
export const PendingCell = styled(NumberCell)`
    color: ${Variables.Swatches.Gray6};
    cursor: help;
`;

/** A text cell the report cannot fill yet (the remark description): a muted dash, with the reason on hover. */
export const PendingText = styled(TableCell)`
    color: ${Variables.Swatches.Gray6};
    cursor: help;
`;

/**
 * Where a core `Notice` sits inside a table card: the card's gutter around it. The notice brings its
 * own surface; this is only spacing (FE-116 · the Notice swap owed since #537).
 */
export const NoticeSlot = styled.div`
    padding: 12px 16px 16px;
`;

/** A notice's sentence with its action beside it — the failed report and its "Try again". */
export const NoticeLine = styled.span`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
`;

/**
 * The week picker beside the two preset buttons.
 *
 * A native `<input type="date">` rather than a calendar popover: the design draws a month grid with
 * the seven-day span highlighted, and a custom calendar is its own component with its own keyboard
 * and screen-reader work. The native control already gives a month grid, keyboard entry, a `max` the
 * browser enforces, and the operator's own locale. The highlighted span is the part not yet built —
 * worth doing when the rest of the page is verified, not before.
 */
export const WeekPickerSlot = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex: none;
`;

export const WeekPickerLabel = styled.label`
    font-size: 12.5px;
    color: ${Variables.ThemeLight.TextXMild};
    white-space: nowrap;
`;


/** The drill-down's heading: the way back, then the row it was opened from. */
export const DrillHeading = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

/**
 * A real button rather than a styled link: it changes view state without navigating, and it has to
 * be reachable and announced as an action, not a destination.
 */
export const DrillBack = styled.button`
    align-self: flex-start;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-size: 12.5px;
    color: ${Variables.Colors.b300};
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${Variables.Colors.b300};
        outline-offset: 2px;
    }
`;

/** A second, quieter line inside a cell — the received date under the payment date. */
export const SubText = styled.div`
    font-size: 11.5px;
    color: ${Variables.ThemeLight.TextXMild};
`;

/* ---- v3 (FE-116, Jordan's revision 3) --------------------------------------------------------- */

const V = Variables.ThemeLight;

/** The coverage summary above the table: the sentence on the left, the filter chips on the right. */
export const SummaryRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    width: 100%;
    padding: 4px 0 12px;
`;

export const SummaryHead = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${V.TextStrong};
`;

export const SummarySub = styled.div`
    margin-top: 4px;
    font-size: 12px;
    color: ${V.TextMild};
`;

/** "Estimated" stays visible, not only in a tooltip: the coverage is our reading of the rules (decision 1). */
export const Estimate = styled.span`
    cursor: help;
    text-decoration: underline dotted;
    text-underline-offset: 3px;
`;

export const SummaryControls = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

// In this theme the WARNING ramp is red and ATTENTION is amber — checked on screen. Partial is amber.
const COVERAGE_DOT = {
    none: V.NeutralConstant6,
    partial: V.AttentionConstant6,
    configured: V.SuccessConstant6,
};

export const CoverageDot = styled.span<{ status: keyof typeof COVERAGE_DOT }>`
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: 6px;
    border-radius: 50%;
    background: ${({ status }) => COVERAGE_DOT[status]};
`;

const COVERAGE_BADGE = {
    none: { bg: V.NeutralMild2, fg: V.NeutralInverted3 },
    partial: { bg: V.AttentionMild2, fg: V.AttentionInverted3 },
    configured: { bg: V.SuccessMild2, fg: V.SuccessInverted4 },
};

export const CoverageBadge = styled.span<{ status: keyof typeof COVERAGE_BADGE }>`
    display: inline-flex;
    align-items: center;
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    white-space: nowrap;
    background: ${({ status }) => COVERAGE_BADGE[status].bg};
    color: ${({ status }) => COVERAGE_BADGE[status].fg};
`;

/** The winning rule's action. Same ramps as the badge, keyed by what the action does. */
const ACTION_PILL: Record<string, { bg: string; fg: string }> = {
    CREATE_MESSAGE: { bg: V.SuccessMild2, fg: V.SuccessInverted4 },
    EXECUTE_ENTER_FUNCTION: { bg: V.PrimaryMild2, fg: V.PrimaryInverted3 },
    SWITCH_TO_PRIVATE_PAY: { bg: V.AttentionMild2, fg: V.AttentionInverted3 },
};

export const ActionPill = styled.span<{ actionType: string }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 11.5px;
    white-space: nowrap;
    background: ${({ actionType }) => (ACTION_PILL[actionType] || { bg: V.NeutralMild2 }).bg};
    color: ${({ actionType }) => (ACTION_PILL[actionType] || { fg: V.NeutralInverted3 }).fg};
`;

/** Badge, pill, rule name and note on the left; the Configure / Edit button on the right. */
export const RulesCell = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 4px 0;
`;

export const RulesText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

export const RulesTags = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;

export const RuleName = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${V.TextStrong};
    overflow-wrap: anywhere;
`;

export const RuleNote = styled.div<{ partial?: boolean }>`
    font-size: 12px;
    line-height: 1.5;
    color: ${({ partial }) => (partial ? V.AttentionInverted3 : V.TextMild)};
    overflow-wrap: anywhere;
`;

/** The code chip and one chip per remark, side by side — never fused into `CO-197.N54`. */
export const CodeRemarkCell = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 4px 0;
`;

export const RemarkChip = styled.span`
    display: inline-block;
    padding: 1px 5px;
    border-radius: 4px;
    border: 1px solid ${V.Border1};
    color: ${V.TextMedium};
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11.5px;
    white-space: nowrap;
`;

export const NoRemark = styled.span`
    font-size: 11.5px;
    color: ${V.TextXMild};
    white-space: nowrap;
`;

/** The CARC text, with the RARC texts under it in the muted line. */
export const ReasonStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 4px 0;
    min-width: 0;
`;

export const ReasonMain = styled.div`
    font-size: 13px;
    line-height: 1.5;
    color: ${V.TextStrong};
`;

export const RemarkText = styled.div`
    font-size: 12px;
    line-height: 1.5;
    color: ${V.TextMild};
`;

/** The week the table shows, beside the controls that choose it — the design's "Sep 25 – Oct 1, 2026". */
export const RangeLabel = styled.span`
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
    white-space: nowrap;
`;

export const TypeSelectSlot = styled.div`
    width: 190px;
    flex: none;
`;

/** Which status type a row is, shown only when the table lists more than one. */
export const TypeTag = styled.span`
    display: inline-block;
    margin-right: 8px;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    vertical-align: 1px;
    background: ${Variables.ThemeLight.NeutralMild2};
    color: ${Variables.ThemeLight.NeutralInverted3};
`;

/** The payer's full message: two lines, the rest on click. */
export const MessageText = styled.div<{ expanded: boolean }>`
    font-size: 12.5px;
    line-height: 1.55;
    color: ${Variables.ThemeLight.TextMild};
    cursor: pointer;
    overflow-wrap: anywhere;
    ${({ expanded }) =>
        expanded
            ? ''
            : `
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    `}
`;

/* ---- v3 drill-down and its ERA card ---------------------------------------------------------- */

export const DrillHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 16px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};
`;

export const DrillTitle = styled.div`
    flex: 1;
    min-width: 260px;
`;

export const Pager = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const Chevron = styled.span`
    display: inline-flex;
    color: ${Variables.ThemeLight.TextXMild};
`;

export const MonoCell = styled.div`
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12.5px;
    color: ${Variables.ThemeLight.TextStrong};
    overflow-wrap: anywhere;
`;

const STATUS_TONE = {
    success: { bg: Variables.ThemeLight.SuccessMild2, fg: Variables.ThemeLight.SuccessInverted4 },
    primary: { bg: Variables.ThemeLight.PrimaryMild2, fg: Variables.ThemeLight.PrimaryInverted3 },
    attention: { bg: Variables.ThemeLight.WarningMild2, fg: Variables.ThemeLight.WarningInverted4 },
    neutral: { bg: Variables.ThemeLight.NeutralMild2, fg: Variables.ThemeLight.NeutralInverted3 },
};

export const StatusBadge = styled.span<{ tone: keyof typeof STATUS_TONE }>`
    display: inline-flex;
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    white-space: nowrap;
    background: ${({ tone }) => STATUS_TONE[tone].bg};
    color: ${({ tone }) => STATUS_TONE[tone].fg};
`;

/** The row's own adjustment: red, as the design draws it. */
export const MatchedAmount = styled.div`
    width: 100%;
    text-align: right;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${Variables.ThemeLight.WarningInverted4};
`;

export const EraFrame = styled.div`
    width: 100%;
    padding: 4px 16px 16px 56px;
    background: ${Variables.ThemeLight.Background2};
`;

export const EraCard = styled.div`
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 8px;
    background: ${Variables.ThemeLight.Background3};
    overflow: hidden;
`;

export const EraHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    padding: 12px 16px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};
`;

export const EraTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${Variables.ThemeLight.TextStrong};

    svg {
        color: ${Variables.ThemeLight.PrimaryConstant6};
    }
`;

export const EraTotals = styled.div`
    display: flex;
    gap: 20px;
    font-size: 12.5px;
    color: ${Variables.ThemeLight.TextMild};

    strong {
        color: ${Variables.ThemeLight.TextStrong};
    }
`;

export const EraMuted = styled.span`
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const EraTable = styled.div`
    display: flex;
    flex-direction: column;
`;

export const EraRow = styled.div<{ head?: boolean; matched?: boolean }>`
    display: grid;
    grid-template-columns: 110px 110px 80px 70px 50px 90px 90px 90px minmax(140px, 1fr) 110px;
    gap: 8px;
    align-items: baseline;
    padding: ${({ head }) => (head ? '8px 16px' : '10px 16px')};
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};
    background: ${({ matched }) => (matched ? Variables.ThemeLight.WarningTranslated1 : 'transparent')};
`;

export const EraHeadCell = styled.span<{ right?: boolean }>`
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMild};
    text-align: ${({ right }) => (right ? 'right' : 'left')};
`;

export const EraCell = styled.span<{ right?: boolean; mono?: boolean; muted?: boolean }>`
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
    color: ${({ muted }) => (muted ? Variables.ThemeLight.TextMild : Variables.ThemeLight.TextStrong)};
    text-align: ${({ right }) => (right ? 'right' : 'left')};
    font-family: ${({ mono }) => (mono ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : 'inherit')};
    overflow-wrap: anywhere;
`;

export const EraCode = styled.span<{ matched: boolean }>`
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-weight: 600;
    color: ${({ matched }) => (matched ? Variables.ThemeLight.WarningInverted4 : Variables.ThemeLight.TextStrong)};
`;

export const EraFoot = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    padding: 10px 16px;
`;

export const EraIds = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
`;

/** A remittance line's control number: one line, cut with an ellipsis, whole on hover. */
export const EraLineId = styled.span`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/** The loading state in the summary line — where the eye is when a week or type is switched. */
export const LoadingLine = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: ${Variables.ThemeLight.TextMedium};
`;

/**
 * The filter spinner, drawn inside the clicked chip's count badge — where the eye and the cursor are.
 * (A spinner beside the bar widened it, the controls wrapped, and the chip just clicked jumped a line.)
 */
export const ChipSpinner = styled.span`
    display: inline-flex;
    width: 1.6em;
    height: 1em;
    line-height: 0;
    justify-content: center;
    align-items: center;
    overflow: visible;

    /* the core Spinner stretches itself; inside a count badge it must not set the badge's height */
    > div {
        align-self: center;
        flex: none;
    }
`;
